import React, { useMemo } from 'react';
import _ from 'underscore';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { StringUtils } from '@bingads-webui-clientcenter/common-utils';
import { TaxTypeFormPropTypes, ViewTaxFormPropTypes } from '../shapes';
import { GlobalTaxForm } from './global-taxform';
import { GlobalViewTaxForm } from './viewtaxforms/global-viewtaxform';
import { LegalIdentifierHintI18nKey, LegalIdentifierNumberPilot, GlobalTaxIdHelpPopUpId, GlobalTaxIdCountryCodeToPilots } from '../constants';
import { registerTaxform } from '../taxform-store';
import { LegalIdentifierNumberField } from '../controls/legal-identitfier-number-field';
import { EmptyViewTaxForm } from './viewtaxforms/empty-viewtaxform';
import { withViewTaxFormWithFilledEmptyTaxValue, withTaxFormByPilot, getPilotStatus, addLegalIdentifierNumberInTaxSchema, getTaxTypeLabel } from '../utils';
import { EmptyTaxForm } from './empty-taxform';
import { useValidationI18n } from '../hooks/use-i18n';
import { CustomValidateItems } from '../validator';

const {
  CountryCode, TaxType, TaxKGVatNumberRegExp, TaxLabelFormat,
} = Constants;
const { regexTest } = StringUtils;
const taxIdValidator = _.partial(regexTest, TaxKGVatNumberRegExp);

const legalIdentifierTaxSchema = {
  name: 'Kyrgyzstan Legal Identifier Validation Schema',
  type: 'object',
  properties: {
    [TaxType.LegalIdentifier]: {
      type: 'string',
      required: true,
    },
  },
};

const taxSchemaGetter = ({ permissions }) => {
  const taxSchema = {
    name: 'Kyrgyzstan Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.VatNumber]: {
        type: 'string',
        required: false,
        helpPopupTopic: GlobalTaxIdHelpPopUpId,
      },
    },
  };

  if (getPilotStatus(LegalIdentifierNumberPilot, permissions)) {
    addLegalIdentifierNumberInTaxSchema(taxSchema);
  }

  return taxSchema;
};

const LegalIdentifierTaxForm = ({
  i18n,
  data,
  onValidate,
  showLineByLineFormGroup,
  alwaysShowError,
}) => {
  const validationI18n = useValidationI18n(i18n);
  return (
    <ValidationGroup
      validationProps={{
        i18n: validationI18n,
        onValidate,
        data,
        schema: legalIdentifierTaxSchema,
      }}
    >
      <LegalIdentifierNumberField
        i18n={i18n}
        showLineByLineFormGroup={showLineByLineFormGroup}
        alwaysShowError={alwaysShowError}
        hintContent={i18n.getString(LegalIdentifierHintI18nKey[CountryCode.KG])}
      />
    </ValidationGroup>
  );
};

LegalIdentifierTaxForm.propTypes = TaxTypeFormPropTypes;

const KGTaxForm = ({
  i18n,
  onValidate,
  showLineByLineFormGroup,
  alwaysShowError,
  data,
  mtData,
  taxCertification,
  mtTaxCertification,
  urlBuilder,
  disabled,
  permissions,
}) => {
  const vatValidatorErrorMessage = useMemo(() => i18n.getString(CustomValidateItems.KyrgyzstanVATFormatValidation), [i18n]);
  const vatLabel = useMemo(() => getTaxTypeLabel(TaxType.VatNumber, i18n, { labelFormat: TaxLabelFormat.RequiredForBusiness, countryCode: CountryCode.KG }, permissions), [i18n, permissions]);

  return (
    <GlobalTaxForm
      i18n={i18n}
      onValidate={onValidate}
      taxSchemaGetter={taxSchemaGetter}
      vatLabel={vatLabel}
      showLineByLineFormGroup={showLineByLineFormGroup}
      vatValidatorErrorMessage={vatValidatorErrorMessage}
      alwaysShowError={alwaysShowError}
      data={data}
      mtData={mtData}
      taxCertification={taxCertification}
      mtTaxCertification={mtTaxCertification}
      urlBuilder={urlBuilder}
      disabled={disabled}
      permissions={permissions}
      taxIdValidator={taxIdValidator}
      showTaxCert={false}
      showLegalIdentifierNumberField
      legalIdentifierHint={i18n.getString(LegalIdentifierHintI18nKey[CountryCode.KG])}
    />
  );
};

KGTaxForm.propTypes = TaxTypeFormPropTypes;

const KGTaxViewTaxForm = props => (
  <GlobalViewTaxForm
    {...props}
    countryCode={CountryCode.KG}
    showTaxCert={false}
  />
);

KGTaxViewTaxForm.propTypes = ViewTaxFormPropTypes;

const LegalIdentifierForm = withTaxFormByPilot({ pilotFlag: LegalIdentifierNumberPilot, PilotForm: LegalIdentifierTaxForm, DefaultForm: EmptyTaxForm });
const LegalIdentifierViewTaxForm = withViewTaxFormWithFilledEmptyTaxValue({ getTaxSchema: () => legalIdentifierTaxSchema });
const LegalIdentifierViewForm = withTaxFormByPilot({ pilotFlag: LegalIdentifierNumberPilot, PilotForm: LegalIdentifierViewTaxForm, DefaultForm: EmptyViewTaxForm });

const remoteSellerPilot = GlobalTaxIdCountryCodeToPilots.KG;
const TaxForm = withTaxFormByPilot({ pilotFlag: remoteSellerPilot, PilotForm: KGTaxForm, DefaultForm: LegalIdentifierForm });
const RemoteSellerViewTaxForm = withViewTaxFormWithFilledEmptyTaxValue({ ViewTaxForm: KGTaxViewTaxForm, getTaxSchema: taxSchemaGetter });
const ViewTaxForm = withTaxFormByPilot({ pilotFlag: remoteSellerPilot, PilotForm: RemoteSellerViewTaxForm, DefaultForm: LegalIdentifierViewForm });

registerTaxform('/KG/', TaxForm, ViewTaxForm);
